import { Clock, Sparkles, ListChecks, BookMarked, CalendarClock, CreditCard, Camera, Film, Gift, Images } from "lucide-react";

function PhoneMock() {
  return (
    <div
      data-testid="app-phone-mock"
      className="relative w-[290px] sm:w-[320px] bg-[#FAF9F6] rounded-[2.4rem] p-3 shadow-[0_60px_120px_-40px_rgba(0,0,0,0.7)] -rotate-2 hover:rotate-0 transition-transform duration-700 mx-auto"
    >
      <div className="bg-[#161514] rounded-[1.9rem] overflow-hidden">
        <div className="px-6 pt-8 pb-6">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#CCBA56] font-bold">Gallery</p>
          <p className="font-display text-2xl text-[#FAF9F6] mt-2 leading-snug">
            Your films are <span className="italic text-[#CCBA56]">ready!</span>
          </p>
          <div className="mt-4 bg-white/10 rounded-xl px-4 py-3 flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-[#8A6D1A] flex items-center justify-center shrink-0">
              <Images className="w-4 h-4 text-[#FAF9F6]" strokeWidth={2} />
            </span>
            <div>
              <p className="text-[12px] text-[#FAF9F6] font-semibold">Sneak Peek delivered</p>
              <p className="text-[11px] text-[#FAF9F6]/55">Tap for your cinematic premiere</p>
            </div>
          </div>
        </div>
        <div className="bg-[#FAF9F6] px-6 py-5 space-y-3">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#1F1E1D]/50 font-bold">Supplier Black Book</p>
          {[["Florist · 15% off"], ["Cake maker · 10% off"], ["Stationery · 20% off"]].map(([row]) => (
            <div key={row} className="flex items-center justify-between">
              <span className="text-[13px] text-[#1F1E1D]/80">{row}</span>
              <span className="text-[10px] font-bold tracking-wide uppercase text-[#8A6D1A]">Reveal code</span>
            </div>
          ))}
        </div>
        <div className="px-6 py-5 border-t border-white/10">
          <div className="bg-[#8A6D1A] text-center py-3.5 rounded-sm">
            <p className="text-[12px] font-bold tracking-[0.14em] uppercase text-[#FAF9F6]">Book Your Pre-Wedding Call</p>
          </div>
        </div>
      </div>
    </div>
  );
}

const FEATURES = [
  { icon: Clock, title: "Countdown & Your Booking", body: "A live countdown to the big day with your package, timings and booking details at your fingertips." },
  { icon: Sparkles, title: "AI Alex", body: "Our smart assistant knows your package, your timeline and everything about your booking. Instant answers, any time of day." },
  { icon: ListChecks, title: "160+ Task Checklist", body: "A full planning checklist across 16 categories. Tasks unlock as your wedding approaches, so you never feel overwhelmed." },
  { icon: BookMarked, title: "Supplier Black Book", body: "Exclusive discounts with our trusted wedding suppliers. The savings alone can cover a chunk of your package." },
  { icon: CalendarClock, title: "Live Wedding Day Timeline", body: "Your complete schedule from hair & makeup to first dance. Share it with family and suppliers in one tap." },
  { icon: CreditCard, title: "Payments & Pay Monthly", body: "Track payments and pay your balance securely, or spread it across monthly payments until your wedding day." },
  { icon: Camera, title: "Guest Photos", body: "A free QR code your guests scan to share every photo they take, all collected in one place." },
  { icon: Film, title: "Gallery Delivery", body: "Your sneak peek and full gallery land straight in the app, with a cinematic premiere moment the first time you press play." },
];

export function AppSection() {
  return (
    <section
      data-testid="wve-app-section"
      data-track-section="wve-app"
      className="bg-[#21201F] text-white px-6 md:px-12 lg:px-16 py-24 md:py-32"
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center mb-16 md:mb-20">
          <div className="lg:col-span-6">
            <div className="text-xs tracking-overline uppercase text-[#A9927D] mb-4 font-medium">
              Free With Every Booking
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight mb-6">
              The WVE App.
              <br />
              <span className="italic font-light">Your wedding companion.</span>
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">
              Every couple who books with us gets our very own app. It's a
              personalised portal that makes planning your wedding calmer,
              easier and a lot more fun.
            </p>
            <p className="text-white/70 leading-relaxed mb-8">
              From the moment you book, it becomes your wedding HQ: your
              countdown, your package, your timeline, your payments and your
              planning checklist, all personalised to you. And when the big day
              is done, this is where the magic lands, with your films delivered
              straight to your phone.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://apps.apple.com/gb/app/wedding-video-essex/id6759569614"
                target="_blank"
                rel="noreferrer"
                data-testid="app-store-badge"
                data-track-click="app-store"
              >
                <img src="https://weddingvideoessex.co.uk/badges/app-store.svg" alt="Download on the App Store" className="h-11" />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=app.emergent.essexweddingappa08b7b5d"
                target="_blank"
                rel="noreferrer"
                data-testid="google-play-badge"
                data-track-click="google-play"
              >
                <img src="https://weddingvideoessex.co.uk/badges/google-play.png" alt="Get it on Google Play" className="h-11" />
              </a>
            </div>
          </div>
          <div className="lg:col-span-6">
            <PhoneMock />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
          {FEATURES.map((f) => (
            <div key={f.title}>
              <f.icon className="w-6 h-6 text-[#CCBA56] mb-4" strokeWidth={1.5} />
              <div className="text-white font-medium mb-2 text-sm tracking-wide">{f.title}</div>
              <p className="text-white/60 text-sm leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center gap-4 text-white/60 text-sm">
          <div className="flex items-center gap-2 text-[#CCBA56]">
            <Gift className="w-4 h-4" strokeWidth={1.5} />
            <span className="uppercase tracking-overline text-[10px] font-medium">The little extras</span>
          </div>
          <p className="leading-relaxed">
            A Time Capsule with secret messages sealed until your first
            anniversary, a wallpaper generator with your names and date, our
            curated wedding playlist, and £500 to split when you refer a friend.
          </p>
        </div>
      </div>
    </section>
  );
}
